import { useNavigate } from "react-router";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  TrendingUp,
  Calendar,
  LogOut,
  CloudDownload,
  Settings,
  Banknote,
  HelpCircle,
  ArrowLeftRight,
  Megaphone,
  Wallet,
  Users,
  Inbox,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import NavigationSection from "./NavigationSection";
import PalommaLogo from "./PalommaLogo";
import SideMenuAccount from "./SideMenuAccount";
import { cn } from "@/lib/utils";
import { useStytchB2BClient } from "@stytch/react/b2b";
import MerchantContext from "../contexts/MerchantContext";
import UserContext from "../contexts/UserContext";
import OrganizationSwitcher from "./OrganizationSwitcher";
import { trpc } from "../trpc";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  isDataScopedEmployee,
  isRestrictedEmployee,
  RESTRICTED_EMPLOYEE_ALLOWED_PATHS,
} from "@/utils/permissions";

const SIDE_MENU_EXPANDED_KEY = "sideMenuExpanded";

const SideMenu = (): JSX.Element => {
  const stytch = useStytchB2BClient();
  const { helpCenterUrl, displayName, broadcastEnabled, collectionsEnabled } =
    useContext(MerchantContext);
  const { roles, email, isLoading: isUserLoading } = useContext(UserContext);
  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);

  // Collapsed by default; the choice is per-browser and outlives the session.
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      return localStorage.getItem(SIDE_MENU_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDE_MENU_EXPANDED_KEY, String(isExpanded));
    } catch {
      // Private-mode browsers reject writes; the rail just won't remember.
    }
  }, [isExpanded]);

  const handleToggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

  const navigate = useNavigate();
  const handleLogout = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/signup");
  }, [stytch, navigate]);

  // Get user info including organizations
  const { data: userInfo } = trpc.auth.getUserInfo.useQuery();
  const { data: orgsData } = trpc.auth.getMemberOrganizations.useQuery();

  const hasMultipleOrgs = orgsData?.organizations && orgsData.organizations.length > 1;
  const isDataScoped = isDataScopedEmployee(roles);
  const isRestricted = isRestrictedEmployee(roles);

  const sideMenuUpperSection = [
    {
      path: "/analytics",
      section: "Analítica",
      icon: TrendingUp,
    },
    {
      path: "/activities",
      section: "Actividades",
      icon: Calendar,
    },
    {
      path: "/inbox",
      section: "Bandeja",
      icon: Inbox,
    },
    {
      path: "/tablero",
      section: "Tablero",
      icon: Users,
    },
    ...(broadcastEnabled
      ? [
          {
            path: "/broadcast",
            section: "Envío masivo",
            icon: Megaphone,
          },
        ]
      : []),
    ...(collectionsEnabled
      ? [
          {
            path: "/recaudo",
            section: "Recaudo",
            icon: Wallet,
          },
        ]
      : []),
    {
      path: "/reports",
      section: "Reportes",
      icon: CloudDownload,
    },
    // Only show Consumption for non data-scoped employees
    ...(!isDataScoped
      ? [
          {
            path: "/consumption",
            section: "Consumo",
            icon: Banknote,
          },
        ]
      : []),
  ];

  // Restricted employees may only reach RESTRICTED_EMPLOYEE_ALLOWED_PATHS.
  const visibleUpperSection = isRestricted
    ? sideMenuUpperSection.filter((item) =>
        RESTRICTED_EMPLOYEE_ALLOWED_PATHS.includes(item.path)
      )
    : sideMenuUpperSection;

  const sideMenuBottomSection = [
    // Only show configuration for non data-scoped employees
    ...(!isDataScoped
      ? [
          {
            path: "/configuration",
            section: "Configuración",
            icon: Settings,
          },
        ]
      : []),
    ...(helpCenterUrl
      ? [
          {
            path: helpCenterUrl,
            section: "Centro de ayuda",
            icon: HelpCircle,
            isExternal: true as const,
          },
        ]
      : []),
    ...(hasMultipleOrgs
      ? [
          {
            path: "/switch-org",
            section: "Cambiar organización",
            icon: ArrowLeftRight,
            handler: "switchOrg" as const,
          },
        ]
      : []),
    {
      path: "/logout",
      section: "Cerrar sesión",
      icon: LogOut,
      handler: "logout" as const,
    },
  ];

  const toggleLabel = isExpanded ? "Contraer menú" : "Expandir menú";

  return (
    // z-40 clears the tallest in-content layer (the tablero's z-30 floating bar)
    // so the toggle stays on top, but sits under portalled overlays at z-50+.
    <div
      className={cn(
        "sticky top-0 z-40 h-screen min-h-screen shrink-0 border-r-[1.5px] border-gray-primary transition-[width] duration-200 ease-out motion-reduce:transition-none",
        isExpanded ? "w-64" : "w-[80px]"
      )}
    >
      <TooltipProvider delayDuration={500}>
        <div className="flex h-full flex-col justify-between overflow-y-auto overflow-x-hidden">
          <ul className="space-y-4 p-4">
            <li
              className={cn(
                "mb-8 flex list-none items-center gap-2",
                !isExpanded && "justify-center"
              )}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="shrink-0">
                    <PalommaLogo width={32} height={32} />
                  </div>
                </TooltipTrigger>
                {!isExpanded && (
                  <TooltipContent side="right">{displayName}</TooltipContent>
                )}
              </Tooltip>
              {isExpanded && (
                <span className="min-w-0 flex-1 truncate font-semibold text-palomma-secondary">
                  {displayName}
                </span>
              )}
            </li>

            {visibleUpperSection.map((ele) => (
              <NavigationSection
                key={ele.path}
                path={`${ele.path}`}
                section={ele.section}
                showText={isExpanded}
                icon={ele.icon}
              />
            ))}
          </ul>
          <ul className="space-y-4 p-4">
            {/* Who am I signed in as. Sits at the head of the account cluster
                (configuración / ayuda / cambiar organización / cerrar sesión)
                because that is where someone questioning their session looks. */}
            {(isUserLoading || email) && (
              <li className="list-none border-b border-border pb-4">
                <SideMenuAccount
                  email={email}
                  displayName={displayName}
                  isExpanded={isExpanded}
                />
              </li>
            )}

            {sideMenuBottomSection.map((ele) => (
              <NavigationSection
                key={ele.path}
                path={`${ele.path}`}
                section={ele.section}
                showText={isExpanded}
                icon={ele.icon}
                onClick={
                  ele.handler === "logout"
                    ? handleLogout
                    : ele.handler === "switchOrg"
                    ? () => setIsOrgSwitcherOpen(true)
                    : undefined
                }
                isExternal={ele.isExternal || false}
              />
            ))}
          </ul>
        </div>

        {/* Straddles the rail's right border, vertically centred. Anchored to
            the rail (sticky is a positioned element) and deliberately a sibling
            of the scroll container above, which clips its overflow. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={toggleLabel}
              aria-expanded={isExpanded}
              onClick={handleToggleExpanded}
              className="absolute -right-3 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-gray-primary bg-white text-palomma-secondary shadow-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-palomma-primary focus-visible:ring-offset-2"
            >
              {isExpanded ? (
                <ChevronLeft className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">{toggleLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Organization Switcher Modal */}
      <OrganizationSwitcher
        isOpen={isOrgSwitcherOpen}
        onClose={() => setIsOrgSwitcherOpen(false)}
        currentOrganizationId={userInfo?.organization?.id}
      />
    </div>
  );
};

export default SideMenu;
